import { useMemo } from "react";

function hashSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || 1;
}

function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function getItemsFingerprint<T>(items: readonly T[]): string {
  return items
    .map((item) => {
      if (item && typeof item === "object" && "id" in item) {
        return String((item as { id: unknown }).id);
      }
      return String(item);
    })
    .join("|");
}

// Fisher–Yates shuffle with a stable seed (same input => same output).
export function shuffleArrayWithSeed<T>(
  arr: readonly T[],
  seedKey: string,
): T[] {
  const result = [...arr];
  const random = createSeededRandom(hashSeed(seedKey));
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Random shuffle (non-deterministic).
export function shuffleArray<T>(arr: readonly T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function useShuffledItems<T>(
  items: readonly T[],
  messageId: string,
  blockIndex: number,
  tabId: string,
  scope?: string,
): T[] {
  const instanceKey = scope
    ? `${messageId}:${blockIndex}:${tabId}:${scope}`
    : `${messageId}:${blockIndex}:${tabId}`;
  const itemsFingerprint = getItemsFingerprint(items);
  const seedKey = `${instanceKey}:${itemsFingerprint}`;

  return useMemo(
    () => shuffleArrayWithSeed(items, seedKey),
    [items, seedKey],
  );
}
